
import React from 'react'
import { Grid, Typography } from '@mui/material'
import { withComponentContext } from 'widgets-shared/core'
import { compose } from 'widgets-shared/core'
import MarketLine from 'smartstream-widgets/components/MarketLine/MarketLine'
import GSCTA from 'smartstream-widgets/controls/GSCTA/GSCTA'
import SinglePredictedScore from './SinglePredictedScore/SinglePredictedScore'
import { homeFirstCompetitions } from 'smartstream-widgets/constants/shared'
import FixtureHeader from 'smartstream-widgets/components/FixtureHeader/FixtureHeader'
import { withTranslations } from 'widgets-shared/core'
import useMatchedBreakpointValues from 'smartstream-widgets/hooks/useMatchedBreakpointValues/useMatchedBreakpointValues'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { Breakpoint } from '@mui/system'
import { PredictedFixture, PredictedCompetitors } from 'smartstream-widgets/widgets/PredictedScore/types'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { Bookmakers } from 'smartstream-widgets/types/sharedModule/core'
import CTAOffer from 'smartstream-widgets/components/CTAOffer/CTAOffer'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'
import { withComponentContextProvider } from 'widgets-shared/core'

type Props = {
  ctaOfferIsDisabledOnWidget: boolean,
  hideCtaOfferOnWidget?: (() => void),
  bookmakers: Bookmakers
  classes: {
    [K in string]: string
  },
  className: string,
  config: {
    BASE_ASSET_PATH: string,
    clickInteraction?: string,
    defaultLink?: string,
    enableCommentary?: boolean,
    CTA?: {
      link: string
    },
    isBookmakerLinkEnabled: boolean,
    showNamesOnHeader: boolean
  },
  containerRef: React.RefObject<HTMLDivElement>,
  data: PredictedFixture,
  theme: Theme,
  translations: {
    [K in string]: string
  }
}

type LogoFlagOverrides = {
  logoSize: number
}

const styles = (theme: Theme) => createStyles({
  affiliateText: {
    color: theme.palette.text.secondary,
    [theme.breakpoints.up('c420')]: {
      fontSize: 10
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 12
    }
  },
  caption: {
    color: theme.palette.grey['700'],
    fontSize: 9,
    padding: `0px ${theme.spacing(1)}`
  },
  captionOdd: {},
  container: {
    height: '100%',
    minHeight: 250,
    '&::after': {
      content: '\'\'',
      display: 'block',
      height: 30
    }
  },
  cta: {
    margin: `${theme.spacing(1)} auto`,
    minHeight: 33
  },
  header: {
    height: 'auto',
    paddingTop: 0
  },
  logo: {
    [theme.breakpoints.up('c420')]: {
      width: 60
    },
    [theme.breakpoints.up('c576')]: {
      height: 25,
      width: 92
    }
  },
  matchState: {
    '& p': {
      [theme.breakpoints.up('c420')]: {
        fontSize: 11
      },
      [theme.breakpoints.up('c728')]: {
        fontSize: 12
      }
    }
  },
  oddButton: {
    [theme.breakpoints.up('c420')]: {
      minWidth: 110
    },
    [theme.breakpoints.up('c576')]: {
      minWidth: 132
    }
  },
  oddButtonDisabled: {},
  odds: {
    justifyContent: 'space-around',
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
    [theme.breakpoints.up('c728')]: {
      padding: '8px 56px'
    },
    [theme.breakpoints.down(301)]: {
      padding: '4px 12px'
    }
  },
  oddsContainerDraw: {
    [theme.breakpoints.up('c420')]: {
      justifyContent: 'space-around'
    }
  },
  oddsDraw: {
    padding: '0 24px',
    gridTemplateColumns: '0px 1fr',
    [theme.breakpoints.up('c728')]: {
      padding: '0 56px',
    }
  },
  scoreLiveCurrent: {
    [theme.breakpoints.up('c420')]: {
      fontSize: 20
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 32
    }
  },
  scoreLivePredicted: {
    [theme.breakpoints.up('c420')]: {
      fontSize: 28
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 40
    }
  },
  scorePredicted: {
    [theme.breakpoints.down(301)]: {
      fontSize: 32
    },
    [theme.breakpoints.up('c420')]: {
      fontSize: 44
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 46
    }
  },
  scoresContainer: {
    height: 100,
    padding: `10px ${theme.spacing(3)}`,
    '&::before': {
      background: theme.palette.divider,
      content: '\'\'',
      display: 'block',
      flexShrink: 0,
      margin: `-5px 20px`,
      order: 1,
      width: 2
    },
    '& *:last-child': {
      order: 2
    },
    [theme.breakpoints.down(301)]: {
      height: 80,
      padding: "4px 16px"
    }
  },
  scoreLabel: {
    [theme.breakpoints.up('c420')]: {
      fontSize: 9
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 10
    }
  },
  scoreLabelRight: {
    [theme.breakpoints.up('c576')]: {
      bottom: 44
    }
  },
  teamContent: {
    [theme.breakpoints.up('c420')]: {
      maxWidth: 150
    },
    [theme.breakpoints.up('c576')]: {
      maxWidth: 170
    }
  },
  teamNames: {
    [theme.breakpoints.up('c420')]: {
      fontSize: 14
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 18
    }
  },
  title: {
    [theme.breakpoints.up('c576')]: {
      fontSize: 16
    },
    [theme.breakpoints.down('c576')]: {
      fontSize: 12
    }
  },
  dateCaption: {
    [theme.breakpoints.down('c576')]: {
      fontSize: 8
    }
  },
  logoFlagSize: {
    [theme.breakpoints.up('c420')]: {
      height: 48,
      width: 100
    },
    [theme.breakpoints.up('c576')]: {
      height: 52,
      width: 130
    },
    [theme.breakpoints.up('c728')]: {
      height: 52,
      width: 130
    }
  },
  teamLogoSize: {
    [theme.breakpoints.up('c420')]: {
      height: 40,
      width: 40
    },
    [theme.breakpoints.up('c576')]: {
      height: 46,
      width: 46
    },
    [theme.breakpoints.up('c728')]: {
      width: 48,
      height: 48
    }
  },
  ctaOffer: {
    height: 19
  },
  fixtureContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around'
  }
})

export function findCompetitors(data: PredictedFixture): PredictedCompetitors {
  const predictedScore = data.predictedScore.predictedScore
  const competitors = data.competitors
  const hasSpread = data.markets && data.markets.spread
  const selections = hasSpread ? data.markets.spread && data.markets.spread[0].selections :
    data.markets && data.markets.moneyLine && data.markets.moneyLine[0].selections
  const hasCurrentScores = data.matchSummaries &&
    Number.isInteger(data.matchSummaries.homeScore) &&
    Number.isInteger(data.matchSummaries.awayScore)

  return competitors.reduce((result, competitor, index) => {
    const competitorPredictedScore = predictedScore && predictedScore.find(score => score.competitorId === competitor.competitorId)
    const competitorSelection = selections && selections.find(selection => selection.competitorId === competitor.competitorId)
    const homeOrAway = competitor.homeOrAway || (index ? 'AWAY' : 'HOME')
    const competitorCurrentScore = hasCurrentScores && data.matchSummaries[homeOrAway === 'HOME' ? 'homeScore' : 'awayScore']
    result[homeOrAway] = {
      ...competitor,
      currentScore: competitorCurrentScore || 0,
      predictedScore: (competitorPredictedScore && competitorPredictedScore.prediction) || 0,
      selection: competitorSelection
    }
    return result
  }, {})
}

export function PredictedScoreComponent(props: Props) {
  const {
    ctaOfferIsDisabledOnWidget,
    hideCtaOfferOnWidget,
    bookmakers,
    classes,
    className = '',
    config: {
      clickInteraction,
      defaultLink,
      enableCommentary = false,
      CTA: { link } = {},
      isBookmakerLinkEnabled,
      showNamesOnHeader = false
    },
    containerRef,
    data,
    theme,
    translations
  } = props
  const competitors = findCompetitors(data)
  const predictedWinner = competitors.HOME.predictedScore > competitors.AWAY.predictedScore ? 'HOME' : 'AWAY'
  const isAwayTeamFirst = !homeFirstCompetitions.includes(data.competitionId)
  const matchStatus = (data.matchSummaries && data.matchSummaries.matchStatus) || (data.matchSummaries && data.matchSummaries.phase) || 'PreMatch'
  const totalPoints = data.predictedScore.totalPoints
  const winnerName = competitors[predictedWinner].shortName || competitors[predictedWinner].officialName
  const winnerScore = competitors[predictedWinner].predictedScore
  const isAwayWinning = matchStatus === 'InPlay'
    ? competitors.AWAY.currentScore >= competitors.HOME.currentScore
    : competitors.AWAY.predictedScore >= competitors.HOME.predictedScore
  const isHomeWinning = matchStatus === 'InPlay'
    ? competitors.HOME.currentScore >= competitors.AWAY.currentScore
    : competitors.HOME.predictedScore >= competitors.AWAY.predictedScore
  const { spread, moneyLine } = data && data.markets || {}
  const markets = spread || moneyLine
  const headerInfo = {
    title: translations.widgetTitle,
    helperInfo: {
      title: translations.aboutWidgetTitle,
      content: translations.helperInfoContent,
      disclaimer: translations.helperInfoDisclaimer,
      footerText: translations.poweredByFooterText
    }
  }

  const sourceId = markets && markets[0] && markets[0].sourceId
  const bookMakerSource = bookmakers && bookmakers[sourceId] && bookmakers[sourceId]
  const ctaOffer = bookMakerSource && bookMakerSource.ctaOffer
  const ctaOfferIsEnabled = bookMakerSource && bookMakerSource.ctaOfferIsEnabled

  const logoFlagOverrides: { [size in Breakpoint]?: LogoFlagOverrides } = {
    'c728': {
      logoSize: 48
    },
    'c576': {
      logoSize: 46
    },
    'c420': {
      logoSize: 40
    }
  }

  const singlePredictedScoreClasses = {
    header: classes.teamNames,
    scoreLivePredicted: classes.scoreLivePredicted,
    scoreLiveCurrent: classes.scoreLiveCurrent,
    scorePredicted: classes.scorePredicted,
    content: classes.teamContent,
    scoreLabel: classes.scoreLabel,
    scoreLabelRight: classes.scoreLabelRight
  }

  const styleOverrides = useMatchedBreakpointValues(logoFlagOverrides, theme)

  let matchState: { scoreAway: number, scoreHome: number, phase: string, matchStatus: string }

  if (data.matchSummaries) {
    matchState = {
      scoreAway: data.matchSummaries.awayScore,
      scoreHome: data.matchSummaries.homeScore,
      phase: data.matchSummaries.phase,
      matchStatus: data.matchSummaries.matchStatus
    }
  }
  return (
    <>
      {data &&
        <Grid data-testid="predicted-score-component" container direction='column' className={`${classes.container} ${className}`} wrap='nowrap'>
          <FixtureHeader
            logoFlagOverrides={styleOverrides}
            classes={{
              content: classes.header,
              matchState: classes.matchState,
              title: classes.title,
              dateCaption: classes.dateCaption,
              logoFlagSize: classes.logoFlagSize
            }}
            competitors={data.competitors}
            containerRef={containerRef}
            headerInfo={headerInfo}
            isAwayTeamFirst={isAwayTeamFirst}
            matchState={matchState}
            scheduledStartTime={data.scheduledStartTime}
            showNames={showNamesOnHeader}
            venueName={data.venueName}
            hideLiveScore
          />
          {ctaOffer && ctaOfferIsEnabled &&
            <CTAOffer
              classes={{ root: classes.ctaOffer }}
              offerText={ctaOffer.offerText}
              link={ctaOffer.link}
              linkText={ctaOffer.linkText}
              dismissibleOffer={ctaOffer.dismissibleOffer}
              hideCtaOfferOnWidget={hideCtaOfferOnWidget}
              ctaOfferIsDisabledOnWidget={ctaOfferIsDisabledOnWidget}
              theme={theme}
            />
          }
          <div className={classes.fixtureContainer}>
            <Grid container className={classes.scoresContainer} justifyContent='space-around' wrap='nowrap'>
              <SinglePredictedScore
                classes={singlePredictedScoreClasses}
                competitor={isAwayTeamFirst ? competitors.AWAY : competitors.HOME}
                matchStatus={matchStatus}
                winning={isAwayTeamFirst ? isAwayWinning : isHomeWinning}
              />
              <SinglePredictedScore
                alignRight
                classes={singlePredictedScoreClasses}
                competitor={isAwayTeamFirst ? competitors.HOME : competitors.AWAY}
                matchStatus={matchStatus}
                winning={isAwayTeamFirst ? isHomeWinning : isAwayWinning}
              />
            </Grid>
            { // TODO: Replace with Commentary Engine
              enableCommentary && predictedWinner && totalPoints &&
              <Typography className={classes.caption}>
                We predict total points over {totalPoints} with {winnerName} to
                win by a margin of {winnerScore && (winnerScore - (totalPoints - winnerScore))}, beating the -12.5 spread.
              </Typography>
            }
            {
              clickInteraction === 'markets' &&
              <MarketLine
                bookmakers={bookmakers}
                isBookmakerLinkEnabled={isBookmakerLinkEnabled}
                affiliateLabel='oddsby'
                animationKey={data.fixtureId}
                classes={{
                  affiliateText: classes.affiliateText,
                  buttonDisabled: classes.oddButtonDisabled,
                  captionOdd: classes.captionOdd,
                  logo: classes.logo,
                  oddButton: classes.oddButton,
                  oddButtonWithDraw: classes.oddButton,
                  oddsContainerDraw: classes.oddsContainerDraw,
                  root: classes.odds,
                  rootDraw: classes.oddsDraw
                }}
                competitors={data.competitors}
                showAffiliateLogo
                sourceId={markets && markets[0].sourceId}
                selections={markets && markets[0].selections}
                teamsPosition='HomeVsAway'
                isAwayTeamFirst={isAwayTeamFirst}
              />
            }
          </div>
          {
            clickInteraction === 'cta' && (link || defaultLink) &&
            <GSCTA classes={{ root: classes.cta }} />
          }
        </Grid>
      }
    </>
  )
}

const PredictedScoreComponentComposed = compose(
  withComponentContext,
  withStyles(styles, { name: 'PredictedScoreComponent', withTheme: true, defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      aboutWidgetTitle: t('about-title.translation', {
        defaultValue: 'About Predicted Score',
        context: 'Text to display as title in the Predicted Score info popup'
      }),
      helperInfoContent: t('about-info.translation', {
        defaultValue: 'Bringing you the best in pre-match and inplay sports data, our Predicted Score widget gives you the info you need to fuel your hobby',
        context: 'Text to display as content inside info popup inside Predicted Score widget'
      }),
      helperInfoDisclaimer: t('about-odds.translation', {
        defaultValue: 'Odds are subject to change at any time.',
        context: 'Text to display inside Predicted Score info popup as disclaimer regarding odds update'
      }),
      helperInfoFooterText: t('about-poweredBy.translation', {
        defaultValue: 'Powered by Genius Sports Media',
        context: 'Text to indicate authoring, in the info popup footer inside Predicted Score widget'
      }),
      widgetTitle: t('title.translation', {
        defaultValue: 'Predicted Score', context: 'Text to display as widget title inside Predicted Score widget'
      })
    }
  })
)(PredictedScoreComponent) as React.ElementType

export default PredictedScoreComponentComposed

export const PredictedScoreComponentWithContext = withComponentContextProvider(PredictedScoreComponentComposed) as React.Component
